import {
  collection,
  doc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  writeBatch,
} from "firebase/firestore";
import { db } from "../firebase/firebase";
import type { DayPortion } from "./leaveUtils";

export type WfhEntry = {
  fromDate: string;
  toDate: string;
  reason: string;
  numDays?: string;
  approvalStatus: string;
  timestamp: string;
  userId?: string;
  managerIds?: string[];
  dayPortions?: Record<string, DayPortion>;
  _monthKey?: string;
  _entryId?: string;
};

const getMonthKeysForYear = (year: number): string[] => {
  return Array.from({ length: 12 }, (_, i) =>
    `${year}-${String(i + 1).padStart(2, "0")}`
  );
};

/**
 * Fetch all WFH entries for a user for a given year.
 * Path: organization/{orgId}/WorkFromHomeData/{userId}/{YYYY-MM}
 */
export const fetchWfhEntries = async (
  organizationId: string,
  userId: string,
  year?: number | null
): Promise<WfhEntry[]> => {
  const currentYear = year ?? new Date().getFullYear();
  const monthKeys = getMonthKeysForYear(currentYear);

  const monthSnapshots = await Promise.all(
    monthKeys.map((mk) =>
      getDocs(
        collection(db, `organization/${organizationId}/WorkFromHomeData/${userId}/${mk}`)
      )
    )
  );

  const entries: WfhEntry[] = [];
  monthSnapshots.forEach((snap, idx) => {
    snap.docs.forEach((d) => {
      entries.push({
        ...d.data(),
        _monthKey: monthKeys[idx],
        _entryId: d.id,
      } as WfhEntry);
    });
  });

  return entries;
};

/**
 * Fetch WFH entries for ALL years (2025+).
 */
export const fetchAllWfhEntries = async (
  organizationId: string,
  userId: string
): Promise<WfhEntry[]> => {
  const currentYear = new Date().getFullYear();
  const startYear = 2025;
  const allEntries: WfhEntry[] = [];

  for (let y = startYear; y <= currentYear; y++) {
    const yearEntries = await fetchWfhEntries(organizationId, userId, y);
    allEntries.push(...yearEntries);
  }

  return allEntries;
};

/**
 * Fetch WFH entries for multiple users for a given year.
 */
export const fetchWfhEntriesForUsers = async (
  organizationId: string,
  userIds: string[],
  year?: number
): Promise<Record<string, WfhEntry[]>> => {
  const currentYear = year ?? new Date().getFullYear();
  const monthKeys = getMonthKeysForYear(currentYear);

  const result: Record<string, WfhEntry[]> = {};
  userIds.forEach((uid) => (result[uid] = []));

  const allPromises = userIds.flatMap((uid) =>
    monthKeys.map((mk) =>
      getDocs(
        collection(db, `organization/${organizationId}/WorkFromHomeData/${uid}/${mk}`)
      )
        .then((snap) => ({ uid, mk, snap }))
        .catch((error) => {
          console.error(`Failed to fetch WFH entries for user ${uid} (${mk}):`, error);
          return null;
        })
    )
  );

  const allResults = await Promise.all(allPromises);
  allResults.forEach((entry) => {
    if (!entry) return;
    const { uid, mk, snap } = entry;
    snap.docs.forEach((d) => {
      result[uid].push({
        ...d.data(),
        _monthKey: mk,
        _entryId: d.id,
      } as WfhEntry);
    });
  });

  return result;
};

/**
 * Submit WFH entries to WorkFromHomeData collection.
 */
export const submitWfhEntries = async (
  organizationId: string,
  userId: string,
  entries: WfhEntry[]
): Promise<void> => {
  for (const entry of entries) {
    const entryFromDate = new Date(entry.fromDate);
    const entryMonthKey = `${entryFromDate.getFullYear()}-${String(
      entryFromDate.getMonth() + 1
    ).padStart(2, "0")}`;
    const entryRef = doc(
      collection(
        db,
        `organization/${organizationId}/WorkFromHomeData/${userId}/${entryMonthKey}`
      )
    );
    await setDoc(entryRef, { ...entry, userId });
  }
};

/**
 * Update a single WFH entry's approval status.
 */
export const updateWfhEntryStatus = async (
  organizationId: string,
  userId: string,
  entry: WfhEntry,
  newStatus: string
): Promise<void> => {
  if (entry._monthKey && entry._entryId) {
    const entryRef = doc(
      db,
      `organization/${organizationId}/WorkFromHomeData/${userId}/${entry._monthKey}/${entry._entryId}`
    );
    await updateDoc(entryRef, { approvalStatus: newStatus });
  }
};

/**
 * Update a single WFH entry's fields.
 */
export const updateWfhEntry = async (
  organizationId: string,
  userId: string,
  entry: WfhEntry,
  updates: Partial<Pick<WfhEntry, "fromDate" | "toDate" | "reason" | "numDays" | "managerIds" | "dayPortions">>
): Promise<void> => {
  if (entry._monthKey && entry._entryId) {
    const entryRef = doc(
      db,
      `organization/${organizationId}/WorkFromHomeData/${userId}/${entry._monthKey}/${entry._entryId}`
    );
    await updateDoc(entryRef, updates);
  }
};

/**
 * Delete a single WFH entry.
 */
export const deleteWfhEntry = async (
  organizationId: string,
  userId: string,
  entry: WfhEntry
): Promise<void> => {
  if (entry._monthKey && entry._entryId) {
    const entryRef = doc(
      db,
      `organization/${organizationId}/WorkFromHomeData/${userId}/${entry._monthKey}/${entry._entryId}`
    );
    await deleteDoc(entryRef);
  }
};

/**
 * Bulk update all pending WFH entries for a user.
 */
export const bulkUpdateWfhEntries = async (
  organizationId: string,
  userId: string,
  entries: WfhEntry[],
  currentUserId: string,
  isAdmin: boolean,
  newStatus: string
): Promise<void> => {
  const batch = writeBatch(db);

  for (const entry of entries) {
    const status = (entry.approvalStatus || "Pending") === "Pending";
    const include =
      status &&
      (isAdmin ||
        (Array.isArray(entry.managerIds) &&
          entry.managerIds.includes(currentUserId)));

    if (include && entry._monthKey && entry._entryId) {
      const entryRef = doc(
        db,
        `organization/${organizationId}/WorkFromHomeData/${userId}/${entry._monthKey}/${entry._entryId}`
      );
      batch.update(entryRef, { approvalStatus: newStatus });
    }
  }

  await batch.commit();
};
